import React, { FormEventHandler, MouseEventHandler } from "react";
import { useTranslation } from "react-i18next";
import { Member } from "../common/ObjectTypes";
import SelectInput from "../common/SelectInput";
import { AttentionIcon } from "../common/ProjectIcons";
import { buttonClasses, inputClasses } from "../common/tailwindClassnames";
import ModalShell from "../common/ModalShell";

type MemberModalProps = {
    member: Member,
    existingMember: Member,
    onChange: Function,
    onSelectRole: Function,
    onToggleModal: MouseEventHandler,
    onSave: FormEventHandler,
    newMember: boolean,
    changesMade: boolean,
    currentMember: Member,
    superuser: boolean,
    errors: any,
    orgName?: string,
}

const MemberModal = ({
    member,
    existingMember,
    onChange,
    onSelectRole,
    onToggleModal,
    onSave,
    newMember,
    changesMade,
    currentMember,
    superuser,
    errors,
    orgName,
}: MemberModalProps) => {
    const { t } = useTranslation();

    const canEdit = currentMember.role === "admin" || superuser;
    const title = canEdit ? (newMember ? t("Add User") : t("Update User")) : t("User Details");

    return (
        <ModalShell
            title={title}
            subtitle={orgName}
            onClose={onToggleModal}
            footer={canEdit ? (
                <>
                    <button
                        className={buttonClasses.primary}
                        type="submit"
                        onClick={onSave}
                        disabled={!changesMade}
                    >
                        {newMember ? t("Add") : t("Update")}
                    </button>
                    <button
                        className={buttonClasses.cancel}
                        type="button"
                        onClick={onToggleModal}
                    >
                        {t("Cancel")}
                    </button>
                </>
            ) : undefined}
        >
            <div className="flex flex-col gap-4">
                <div className="flex flex-col w-full">
                    <label className="text-ui-text-primary-text mb-1 transition-colors duration-300">{t("Email")}*</label>
                    <input
                        id="email"
                        className={inputClasses.standard}
                        value={newMember ? member.email : existingMember?.email}
                        onChange={(e) => onChange(e)}
                        disabled={!canEdit}
                    />
                    {errors.email && <div className="flex flex-row text-unlisted-orange-600 mt-1"><AttentionIcon /><p className="ml-1 mt-0.5">{errors.email}</p></div>}
                </div>
                <div className="flex flex-col w-full">
                    <label className="text-ui-text-primary-text mb-2 transition-colors duration-300">{t("Role")}*</label>
                    <SelectInput
                        options={[{ label: t("Admin"), value: "admin" }, { label: t("Viewer"), value: "viewer" }]}
                        className="basic-single"
                        defaultValue={newMember ? { label: t("Select"), value: 0 } : { label: existingMember.role, value: existingMember.role }}
                        isDisabled={!canEdit}
                        isLoading={false}
                        isClearable={false}
                        isSearchable={true}
                        isMulti={false}
                        name="Type"
                        onChange={(e: any) => onSelectRole(e)}
                    />
                    {errors.role && <div className="flex flex-row text-unlisted-orange-600 mt-1"><AttentionIcon /><p className="ml-1 mt-0.5">{errors.role}</p></div>}
                </div>
                <div className="flex flex-col w-full">
                    <label className="text-ui-text-primary-text mb-1 transition-colors duration-300">{t("First Name")}*</label>
                    <input
                        id="first_name"
                        className={inputClasses.standard}
                        value={newMember ? member.first_name : existingMember.first_name}
                        onChange={(e) => onChange(e)}
                        disabled={!canEdit}
                    />
                    {errors.first_name && <div className="flex flex-row text-unlisted-orange-600 mt-1"><AttentionIcon /><p className="ml-1 mt-0.5">{errors.first_name}</p></div>}
                </div>
                <div className="flex flex-col w-full">
                    <label className="text-ui-text-primary-text mb-1 transition-colors duration-300">{t("Last Name")}*</label>
                    <input
                        id="last_name"
                        className={inputClasses.standard}
                        value={newMember ? member.last_name : existingMember.last_name}
                        onChange={(e) => onChange(e)}
                        disabled={!canEdit}
                    />
                    {errors.last_name && <div className="flex flex-row text-unlisted-orange-600 mt-1"><AttentionIcon /><p className="ml-1 mt-0.5">{errors.last_name}</p></div>}
                </div>
            </div>
        </ModalShell>
    );
};

export default MemberModal;
